import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useCallState } from '../context/CallStateContext';
import { usePractice } from '../context/PracticeContext';
import PracticeLogo from './PracticeLogo';
import PvsMaintenanceMenu from './PvsMaintenanceMenu';
import InstallHelpModal from './InstallHelpModal';

// Remembers, per origin, that this browser installed the app — the half of the
// install detection that works no matter where the app is served from.
const INSTALLED_KEY = 'deskbot.appInstalled';
// The "in der App öffnen" button only belongs in a browser tab on a platform that
// can launch a protocol handler: never inside the installed app itself, and never
// on Android, where Chrome/Edge register no protocol handlers at all.
const IS_STANDALONE = !!window.matchMedia?.('(display-mode: standalone)').matches;
const CAN_OPEN_IN_APP = !IS_STANDALONE && !/Android/i.test(navigator.userAgent);

// Which instructions a browser that offers no install event of its own needs.
// Capability first; the user agent is only consulted to tell Safari (which has
// its own "Ablage > Zum Dock hinzufügen" flow) apart from a browser that cannot
// install at all. `navigator.vendor` is Apple's for Safari AND for the iOS
// browsers that are only Safari in a coat, so those are excluded by name.
const INSTALL_HELP_MODE = (() => {
  const ua = navigator.userAgent;
  if (/Apple/i.test(navigator.vendor || '') && !/CriOS|FxiOS|EdgiOS/i.test(ua)) return 'safari';
  if (/Chrome|Chromium|Edg\/|OPR\//i.test(ua)) return 'chromium';
  return 'unsupported';
})();

// Build initials from given/family/preferred_username; fits both
// individual accounts (first+last name) and reception accounts (username only).
const computeInitials = (tp) => {
  const given = (tp?.given_name || '').trim();
  const family = (tp?.family_name || '').trim();
  if (given || family) {
    return ((given[0] || '') + (family[0] || '')).toUpperCase();
  }
  const username = (tp?.preferred_username || '').trim();
  if (!username) return '?';
  // Strip non-letters; first 2 letters of the remainder. Falls back to first
  // 2 chars (so "rezeption" → "RE", "info42" → "IN").
  const cleaned = username.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  return ((cleaned || username).substring(0, 2)).toUpperCase();
};

const computeFullName = (tp) => {
  const given = (tp?.given_name || '').trim();
  const family = (tp?.family_name || '').trim();
  const full = [given, family].filter(Boolean).join(' ');
  return full || tp?.preferred_username || 'Benutzer';
};

// Unique white SVG icons for each nav link
const icons = {
  dashboard: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
  ),
  calendar: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
  ),
  tasks: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M9 9h6M9 13h4"/></svg>
  ),
  callflow: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z"/><path d="M10 7h4M7 10v4M17 10v4M10 17h4"/></svg>
  ),
  config: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.09a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
  ),
  testcall: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><path d="M22 16.92V19a2 2 0 0 1-2.18 2A19.72 19.72 0 0 1 3 5.18 2 2 0 0 1 5 3h2.09a2 2 0 0 1 2 1.72c.13 1.05.37 2.06.72 3.03a2 2 0 0 1-.45 2.11l-.27.27a16 16 0 0 0 6.29 6.29l.27-.27a2 2 0 0 1 2.11-.45c.97.35 1.98.59 3.03.72A2 2 0 0 1 22 16.92z"/></svg>
  ),
  chat: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  ),
  // Session 10c: super_admin-only Praxisverwaltung — layered-squares motif
  // signals platform-level (multi-practice) management.
  practices: (
    <svg width="20" height="20" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" style={{marginRight:8, verticalAlign:'middle'}} aria-hidden="true"><rect x="3" y="3" width="13" height="13" rx="2"/><rect x="8" y="8" width="13" height="13" rx="2"/></svg>
  )
};

const navLinks = [
  { to: '/dashboard', label: 'Dashboard', icon: icons.dashboard, aria: 'Dashboard', adminOnly: false },
  { to: '/tasks', label: 'Aufgaben', icon: icons.tasks, aria: 'Aufgaben', adminOnly: false },
  { to: '/config', label: 'Konfiguration', icon: icons.config, aria: 'Konfiguration', adminOnly: false },
  { to: '/calendar', label: 'Kalender', icon: icons.calendar, aria: 'Kalender', superAdminOnly: true },
  { to: '/call-monitor', label: 'Call Monitor', icon: icons.testcall, aria: 'Call Monitor', adminOnly: false },
  { to: '/chat', label: 'Chat (Dev)', icon: icons.chat, aria: 'Developer Chat', adminOnly: true },
  { to: '/admin/practices', label: 'Praxisverwaltung', icon: icons.practices, aria: 'Praxisverwaltung', superAdminOnly: true },
];

const InstallButton = ({ onClick, title }) => (
  <button type="button" className="pvs-maint-trigger" title={title} aria-label={title} onClick={onClick}>
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
      <path d="M12 3v12" />
      <path d="m7 12 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  </button>
);

const UserMenu = ({ keycloak, onSignOut }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const tp = keycloak?.tokenParsed;
  const initials = computeInitials(tp);
  const fullName = computeFullName(tp);
  const email = tp?.email || '';

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="user-menu">
      <button
        type="button"
        className={`user-menu-trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Konto: ${fullName}`}
      >
        {initials}
      </button>
      {open && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-name">{fullName}</div>
            {email && <div className="user-menu-email">{email}</div>}
          </div>
          <div className="user-menu-divider" />
          <button
            type="button"
            className="user-menu-item"
            onClick={() => { setOpen(false); onSignOut(); }}
            role="menuitem"
          >
            Abmelden
          </button>
        </div>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const { keycloak, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeCall } = useCallState();
  // Session 10: practice selector + super_admin gating come from PracticeContext,
  // which also applies the owner view (Inhaber-Ansicht) to the role checks.
  const {
    practiceId, availablePractices, switchPractice, practiceRole,
    isUserAdmin: userIsAdmin, isUserSuperAdmin: userIsSuperAdmin,
    canUseOwnerView, ownerView, toggleOwnerView,
  } = usePractice();
  // Practice selector visible whenever the user has access to >1 practice OR is super_admin
  // (super_admin always sees every practice, even when only 1 exists).
  const showPracticeSelector = userIsSuperAdmin || (availablePractices?.length || 0) > 1;

  // The tab title (call indicator + unseen-task count) is owned by TaskAlerts,
  // mounted once in App.js. Two components writing document.title overwrote
  // each other: a task arriving during a call blinked invisibly.

  // Install offer. index.js catches beforeinstallprompt before React mounts, so
  // seed from there and also listen in case it arrives later. Chrome/Edge only,
  // and never once installed, so this button shows once and then retires.
  const [installPrompt, setInstallPrompt] = useState(window.deskbotInstallPrompt || null);
  // Once installed Chrome stops firing beforeinstallprompt, so the install button
  // retires and this takes over, offering to launch the installed app through the
  // web+deskbot: protocol handler instead. Two signals feed it: the appinstalled
  // event, which fires on whatever origin serves the app and is remembered here,
  // and getInstalledRelatedApps(), which also catches an install this code never
  // saw but only on an origin listed in the manifest's related_applications.
  const [appInstalled, setAppInstalled] = useState(() => {
    try { return localStorage.getItem(INSTALLED_KEY) === '1'; } catch { return false; }
  });
  const markInstalled = () => {
    try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* ignore */ }
    setAppInstalled(true);
  };
  const [launchFailed, setLaunchFailed] = useState(false);
  // Shown by the same header button whenever this browser has no install event
  // of its own to fire. Before this the button simply vanished there, which is
  // why Safari users never learned the app existed.
  const [installHelpOpen, setInstallHelpOpen] = useState(false);
  const launchTimerRef = useRef(null);
  // Both the browser's "Open DeskBot?" dialog and the opened app window take
  // focus away from this page, so hasFocus() (not visibilityState, which stays
  // 'visible' for either) is what separates a launch from a no-op.
  const openInApp = () => {
    setLaunchFailed(false);
    window.location.href = 'web+deskbot://open';
    clearTimeout(launchTimerRef.current);
    launchTimerRef.current = setTimeout(() => setLaunchFailed(document.hasFocus()), 2000);
  };
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); window.deskbotInstallPrompt = e; setInstallPrompt(e); };
    const onInstalled = () => { window.deskbotInstallPrompt = null; setInstallPrompt(null); markInstalled(); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    // Catches an install this browser did before the flag existed (or in another
    // profile). A negative answer means "no matching entry", not "not installed",
    // so it can only ever set the flag, never clear it.
    if (CAN_OPEN_IN_APP && navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps()
        .then((apps) => { if (apps.some((a) => a.platform === 'webapp')) markInstalled(); })
        .catch(() => {});
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(launchTimerRef.current);
    };
  }, []);

  // Global Cmd+K / Ctrl+K — focus search if on Tasks page, else navigate there.
  useEffect(() => {
    const handleKeydown = (e) => {
      const isModK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K');
      if (!isModK) return;
      e.preventDefault();
      const searchInput = document.querySelector('input[placeholder="Suchen..."]');
      if (searchInput) {
        searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        searchInput.focus();
        searchInput.select?.();
      } else {
        navigate('/tasks', { state: { focusSearch: true } });
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [navigate]);

  // Ctrl+Shift+E flips Admin-/Inhaber-Ansicht. Registered only for accounts that
  // have the switch, so everyone else keeps the browser's own binding.
  useEffect(() => {
    if (!canUseOwnerView) return undefined;
    const handleKeydown = (e) => {
      if (!e.ctrlKey || !e.shiftKey || (e.key !== 'e' && e.key !== 'E')) return;
      e.preventDefault();
      if (!e.repeat) toggleOwnerView();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [canUseOwnerView, toggleOwnerView]);

  // Filter nav links based on admin / super_admin status.
  // Links default to visible; adminOnly requires admin role, superAdminOnly requires super_admin.
  // task_manager (per-practice role) sees ONLY the Tasks page (super_admin is never
  // restricted). Backend enforces the same restriction on the API.
  const isTaskManager = practiceRole === 'task_manager' && !userIsSuperAdmin;
  const visibleNavLinks = navLinks.filter(link => {
    if (isTaskManager) return link.to === '/tasks';
    if (link.superAdminOnly && !userIsSuperAdmin) return false;
    if (link.adminOnly && !userIsAdmin) return false;
    return true;
  });

  // Fallback sign out if not present
  const handleSignOut = () => {
    if (signOut) {
      signOut();
      navigate('/');
    } else if (keycloak && keycloak.logout) {
      keycloak.logout();
      navigate('/');
    } else {
      window.location.href = '/';
    }
  };

  return (
    <div className="layout">
      <header className="main-header" style={{ background: 'var(--primary-nav-bg)' }}>
        {/* Logo with favicon */}
        <span className="navbar-logo" style={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '1.5rem', color: '#fff', letterSpacing: '-1px', marginRight: 32, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/Deskbot-newlogo-favicon.png" alt="DeskBot Logo" style={{ height: '32px', width: '32px' }} />
          DeskBot
        </span>
        <nav className="navbar-nav nav-links" aria-label="Main navigation">
          {visibleNavLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
              aria-label={link.aria}
              // A plain click on the page you are already on does nothing: navigating
              // would drop its query (e.g. ?tab=). Modified clicks still open a new tab.
              onClick={(e) => {
                if (link.to === pathname && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) e.preventDefault();
              }}
            >
              {/* For Call Monitor: show blinking indicator when call active, phone icon otherwise */}
              {link.to === '/call-monitor' ? (
                activeCall ? (
                  <span
                    className="listening-blink"
                    style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      backgroundColor: 'var(--state-active)',
                      borderRadius: '50%',
                      marginRight: 8,
                      verticalAlign: 'middle'
                    }}
                  />
                ) : (
                  link.icon
                )
              ) : (
                link.icon
              )}
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div style={{ flex: 1 }} />
        {showPracticeSelector && (
          <select
            value={practiceId || ''}
            onChange={(e) => switchPractice(e.target.value)}
            aria-label="Praxis wechseln"
            style={{
              maxWidth: 220,
              marginRight: 12,
              padding: '6px 10px',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 6,
              fontSize: '0.9rem',
              cursor: 'pointer',
            }}
          >
            {availablePractices.map((p) => (
              <option key={p.practice_id} value={p.practice_id} style={{ color: '#000' }}>
                {p.practice_name}
              </option>
            ))}
          </select>
        )}
        {/* Trailing header controls. Spacing lives in .header-actions (App.css),
            which also zeroes .pvs-maint's own margin-right so all the icons sit
            an equal distance apart. */}
        <div className="header-actions">
        {/* Shown by the REAL role, so it stays reachable in the owner view. */}
        {canUseOwnerView && (
          <button
            type="button"
            className="pvs-maint-trigger view-mode-bubble"
            title="Ansicht wechseln (Strg+Umschalt+E)"
            onClick={toggleOwnerView}
          >
            {ownerView ? 'Inhaber-Ansicht' : 'Admin-Ansicht'}
          </button>
        )}
        {launchFailed && (
          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem', maxWidth: 200, lineHeight: 1.25 }}>
            App nicht gefunden. Bitte einmal neu installieren.
          </span>
        )}
        {installPrompt ? (
          <InstallButton
            title="DeskBot als App installieren"
            onClick={() => { installPrompt.prompt(); setInstallPrompt(null); }}
          />
        ) : (appInstalled && CAN_OPEN_IN_APP) ? (
          <button
            type="button"
            className="pvs-maint-trigger"
            title="DeskBot in der App öffnen"
            aria-label="DeskBot in der App öffnen"
            onClick={openInApp}
          >
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <path d="M15 3h6v6" />
              <path d="M10 14 21 3" />
              <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
            </svg>
          </button>
        ) : (!IS_STANDALONE && !appInstalled) ? (
          // Anything that fires no install event of its own gets instructions.
          // `appInstalled` can still be true here on Android, where branch 2 is
          // off by design; telling those users to install it again would be wrong.
          <InstallButton
            title="DeskBot als App installieren"
            onClick={() => setInstallHelpOpen(true)}
          />
        ) : null}
        <PvsMaintenanceMenu />
        <UserMenu keycloak={keycloak} onSignOut={handleSignOut} />
        </div>
      </header>
      {installHelpOpen && (
        <InstallHelpModal mode={INSTALL_HELP_MODE} onClose={() => setInstallHelpOpen(false)} />
      )}
      {/* Bottom padding grows only while the support chat's floating starter is shown
          (components/support/SupportWindow.css sets the variable), so it never covers the last row. */}
      <main className="main-content" style={{ padding: '28px 32px var(--support-starter-clearance, 36px) 32px', margin: 0, background: 'transparent', borderRadius: 0, minHeight: 'calc(100vh - 90px)', position: 'relative' }}>
        <PracticeLogo />
        {children}
      </main>
    </div>
  );
};

export default Layout; 